import { ColumnDef } from "@tanstack/react-table";
import { EOrderStatus } from "constants/enums";
import { formatDateTimeByLocale } from "utils/formattingUtil";

import { OrderCellStatus } from "./Components/OrderCellStatus";
import { TableCellEllipsis } from "./Components/TableCellEllipsis";
import { sortByDate, sortByName, sortByPrice, sortByStatus } from "./sortingUtils";
import { OrderTableRow } from "./utils";

export const ORDER_TABLE_COLUMNS: ColumnDef<OrderTableRow>[] = [
    {
        accessorKey: 'project',
        header: 'Project Name',
        cell: ({ getValue }) => <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>,
        enableSorting: true
    },
    {
        accessorKey: 'name',
        header: 'Offer Name',
        cell: ({ getValue }) => <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>,
        sortingFn: sortByName,
        enableSorting: true
    },
    {
        accessorKey: 'price',
        header: 'Price',
        cell: ({ getValue, row }) => <TableCellEllipsis>{row.original.currencySymbol}{getValue()?.toString()}</TableCellEllipsis>,
        sortingFn: sortByPrice,
        sortDescFirst: true,
        enableSorting: true
    },
    {
        accessorKey: 'date',
        header: 'Date',
        cell: ({ getValue }) => <TableCellEllipsis>{formatDateTimeByLocale(getValue() as string)}</TableCellEllipsis>,
        sortingFn: sortByDate,
        sortDescFirst: true,
        enableSorting: true
    },
    {
        accessorKey: 'orderId',
        header: 'Order ID',
        cell: ({ getValue }) => <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    },
    {
        accessorKey: 'playerId',
        header: 'Player ID',
        cell: ({ getValue }) => <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    },
    {
        accessorKey: 'subscriptionId',
        header: 'Subscription ID',
        cell: ({ getValue }) => <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    },
    {
        accessorKey: 'publisherPurchaseId',
        header: 'Transaction ID',
        cell: ({ getValue }) => <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    },
    {
        accessorKey: 'paymentFlow',
        header: 'Payment Flow',
        cell: ({ getValue }) => <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    },
    {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <OrderCellStatus {...row.original} />,
        sortingFn: sortByStatus,
        enableSorting: true
    },
]

export const FREE_ORDER_TABLE_COLUMNS: ColumnDef<OrderTableRow>[] = [
    {
        accessorKey: 'project',
        header: 'Project',
        cell: ({ getValue }) => <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>,
        enableSorting: true
    },
    {
        accessorKey: 'date',
        header: 'Date',
        cell: ({ getValue }) => <TableCellEllipsis>{formatDateTimeByLocale(getValue() as string)}</TableCellEllipsis>,
        sortingFn: sortByDate,
        sortDescFirst: true,
        enableSorting: true
    },
    {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ getValue }) => <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    },
    {
        accessorKey: 'price',
        header: 'Price',
        cell: () => <TableCellEllipsis>Free</TableCellEllipsis>,
        enableSorting: false
    },
    {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ getValue }) => <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>,
        sortingFn: sortByName,
        enableSorting: true
    },
    {
        accessorKey: 'orderId',
        header: 'Order ID',
        cell: ({ getValue }) => <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    },
    {
        accessorKey: 'playerId',
        header: 'Player ID',
        cell: ({ getValue }) => <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    },
    {
        accessorKey: 'publisherPurchaseId',
        header: 'Transaction ID',
        cell: ({ getValue }) => <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    },
    {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <OrderCellStatus {...row.original} />,
        sortingFn: sortByStatus,
        enableSorting: true
    },
]

export enum OrderFilterKey {
    STATUSES = 'statuses',
    PUBLISHER_PURCHASE_ID = 'publisherPurchaseId',
    RECEIPT_ID = 'receiptId',
    ORDER_ID = 'orderId',
    PLAYER_ID = 'playerId',
    SUBSCRIPTION_ID = 'subscriptionId',
    PROJECTS = 'projects',
    TRANSACTION_ID = 'transactionId',
    PAYMENT_FLOW = 'paymentFlow',
    START_DATE = 'startDate',
    END_DATE = 'endDate',
    IS_FREE = 'isFree',
}

export const VALID_ORDER_STATUSES = [
    EOrderStatus.created,
    EOrderStatus.payment_pending,
    EOrderStatus.payment_succeed,
    EOrderStatus.payment_failed,
    EOrderStatus.payment_refund_pending,
    EOrderStatus.payment_refund_requires_action,
    EOrderStatus.payment_refunded,
    EOrderStatus.payment_refund_failed,
    EOrderStatus.payment_refund_canceled,
    EOrderStatus.payment_canceled,
    EOrderStatus.charge_pending,
    EOrderStatus.charge_succeed,
    EOrderStatus.charge_failed,
    EOrderStatus.payment_dispute_open,
    EOrderStatus.payment_dispute_lost,
    EOrderStatus.payment_dispute_won,
    EOrderStatus.payment_dispute_evidence_submitted,
    EOrderStatus.payment_dispute_inquiry_open,
    EOrderStatus.payment_dispute_inquiry_evidence_submitted,
    EOrderStatus.payment_dispute_inquiry_won
]

export const ORDER_TABLE_ROWS_PER_PAGE = 200

export const ORDER_TYPE_OPTIONS = [
    { label: 'Free Orders', value: 'true' },
    { label: 'Paid Orders', value: 'false' }
]